import { useEffect, useSyncExternalStore } from 'react';

/**
 * The one voice note playing, owned by the page rather than by a bubble.
 *
 * Each AudioNote used to carry its own <audio> element, so the note stopped
 * the moment its bubble went away - and bubbles go away a lot: coming back to
 * the browser tab rebuilds the chat window (Chats.js bumps threadVersion),
 * opening another chat or page unmounts it, a refetch can remount it. Playback
 * lives here instead, in an element React never renders, and only ends when
 * the note does, the listener stops it, or another note starts - one at a
 * time, as on WhatsApp.
 *
 * Bubbles are views of this: any AudioNote for the same URL, including one
 * mounted after playback began, shows the same progress and controls. When no
 * bubble for the note is on screen, VoiceMiniPlayer shows it instead.
 */

// WhatsApp's speeds. Shared by every note: someone who listens at 2x wants the
// next note at 2x too, not to set it again on every message.
export const RATES = [1, 1.5, 2];

/**
 * Where a note lives, for the floating player: who sent it, which chat or task
 * it is in, and how to get back there.
 *   { title, subtitle, href, messageId }
 */
const IDLE = { url: null, playing: false, currentTime: 0, duration: 0, rate: 1, origin: null };

let element = null;
let state = IDLE;
// url -> how many of its bubbles are on screen right now.
const visible = new Map();
let visibleVersion = 0;
const listeners = new Set();

const notify = () => listeners.forEach((listener) => listener());

const emit = (patch) => {
    state = { ...state, ...patch };
    notify();
};

/** Created on first use: nothing is fetched until somebody presses play. */
const audio = () => {
    if (element) return element;
    element = new Audio();
    element.preload = 'none';
    const finite = (value) => (Number.isFinite(value) ? value : 0);
    element.addEventListener('timeupdate', () => emit({ currentTime: element.currentTime, duration: finite(element.duration) }));
    element.addEventListener('loadedmetadata', () => emit({ duration: finite(element.duration) }));
    element.addEventListener('play', () => emit({ playing: true }));
    element.addEventListener('pause', () => emit({ playing: false }));
    // Back to the start, as the bubble always did: a finished note reads as unplayed.
    element.addEventListener('ended', () => emit({ playing: false, currentTime: 0 }));
    return element;
};

const subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

/** Play or pause this note. Starting it stops whichever one was playing. */
export const toggleVoiceNote = (url, origin = null) => {
    const el = audio();
    if (state.url === url) {
        if (el.paused) {
            el.playbackRate = state.rate;
            el.play().catch(() => emit({ playing: false }));
        } else {
            el.pause();
        }
        if (origin) emit({ origin });
        return;
    }
    if (!el.paused) el.pause();
    el.src = url;
    el.playbackRate = state.rate;
    emit({ url, origin, playing: true, currentTime: 0, duration: 0 });
    el.play().catch(() => emit({ playing: false }));
};

/** The floating player's close: stop, rewind, and forget the note. */
export const stopVoiceNote = () => {
    if (!element || !state.url) return;
    if (!element.paused) element.pause();
    element.removeAttribute('src');
    element.load();
    emit({ ...IDLE, rate: state.rate });
};

/** Jump to a fraction of the note - only the one loaded, since that is the only one with a position. */
export const seekVoiceNote = (url, ratio) => {
    const el = audio();
    if (state.url !== url || !el.duration || !Number.isFinite(el.duration)) return;
    el.currentTime = ratio * el.duration;
    emit({ currentTime: el.currentTime });
};

export const cycleVoiceRate = () => {
    const rate = RATES[(RATES.indexOf(state.rate) + 1) % RATES.length];
    audio().playbackRate = rate;
    emit({ rate });
};

/** What the page should show for this note: live state if it is the loaded one, idle otherwise. */
export const useVoicePlayback = (url) => {
    const current = useSyncExternalStore(subscribe, () => state);
    return current.url === url ? current : { ...IDLE, rate: current.rate };
};

/**
 * Report whether a bubble for `url` is on screen. The floating player only
 * shows while none is: a note you can already see needs no second control.
 */
export const useReportVisible = (url, isVisible) => {
    useEffect(() => {
        if (!isVisible || !url) return undefined;
        visible.set(url, (visible.get(url) || 0) + 1);
        visibleVersion += 1;
        notify();
        return () => {
            const left = (visible.get(url) || 1) - 1;
            if (left > 0) visible.set(url, left); else visible.delete(url);
            visibleVersion += 1;
            notify();
        };
    }, [url, isVisible]);
};

/** The floating player's view: the loaded note, and whether one of its bubbles is already on screen. */
export const useVoiceDock = () => {
    const current = useSyncExternalStore(subscribe, () => state);
    useSyncExternalStore(subscribe, () => visibleVersion);
    return { ...current, onScreen: Boolean(current.url && visible.get(current.url)) };
};

/** For tests: the element doing the playing. */
export const voicePlaybackElement = () => audio();
